<template>
  <div id="app">
    <router-view v-cloak :key="currentLocale" />
    <vue-progress-bar />
    <vue-snotify />
    <FeaturesUpdateModal v-if="isLoggedIn" />
  </div>
</template>

<script>
import FeaturesUpdateModal from '@components/common/FeaturesUpdateModal.vue';
import router from './router';
import store from './store';
import ApiService from "@services/api.service";
import Lang from '@/lang';
import eBus from '@/event-bus';
import { mapState, mapGetters, mapActions } from 'vuex';
import mods from '../../.smsconfig.json';

function getCurrentLang() {
  return document.documentElement.lang || localStorage.getItem('locale') || 'ar';
}
function setHtmlLangDir(lang) {
  document.documentElement.setAttribute('lang', lang);
  document.documentElement.setAttribute('dir', lang.startsWith('ar') ? 'rtl' : 'ltr');
}
function appNameFor(lang) {
  const storeName = store.getters['settings/systemName'];
  if (storeName) return storeName;
  return lang.startsWith('ar') ? 'سكولنا' : 'Schoolna';
}

function findSidebarLabel(links, path) {
  for (const item of links) {
    if (item.path && item.path === path) return item.label;
    if (item.children) {
      const found = findSidebarLabel(item.children, path);
      if (found) return found;
    }
  }
  return null;
}

function titleForLogin(lang, appName) {
  const loginText = lang.startsWith('ar') ? 'تسجيل الدخول' : 'Login';
  return `${appName} - ${loginText}`;
}

function titleForLanding(lang, appName) {
  return `${appName} - ${lang.startsWith('ar') ? 'الصفحة الرئيسية' : 'Landing'}`;
}

function titleForDashboard(lang, appName) {
  return `${appName} - ${lang.startsWith('ar') ? 'الرئيسية' : 'Dashboard'}`;
}

function computeTitle(to, isLoggedIn = false) {
  const lang = getCurrentLang();
  const appName = appNameFor(lang);

  if (to.path === '/') {
    return isLoggedIn ? titleForDashboard(lang, appName) : titleForLogin(lang, appName);
  }
  if (to.name === 'login') {
    return titleForLogin(lang, appName);
  }
  if (to.name === 'landing' || to.path === '/landing') {
    return titleForLanding(lang, appName);
  }

  const side = (mods && mods.side_menu) || {};
  const allLinks = [
    ...(side.systemAdminLinks || []),
    ...(side.schoolLinks || []),
    ...(side.teachersLinks || []),
  ];
  const label = findSidebarLabel(allLinks, to.path);
  if (label) {
    return `${appName} - ${Lang.get(`messages.sidebar.${label}`)}`;
  }

  let fallback = to.name ? to.name.replace(/-/g, '_') : decodeURIComponent((to.path.split('/').pop() || '').trim());
  fallback = fallback.replace(/_enhanced$/, '');
  if (fallback.startsWith('messages.')) return appName;

  const translated = Lang.get(`messages.${fallback}`);
  return `${appName} - ${translated || fallback || appName}`;
}

router.afterEach((to) => {
  const lang = getCurrentLang();
  setHtmlLangDir(lang);
  const isLoggedIn = store.getters['users/isLoggedIn'];
  document.title = computeTitle(to, isLoggedIn);
});

export default {
  name: "App",
  components: {
    FeaturesUpdateModal
  },
  data() {
    return {
      currentLocale: getCurrentLang()
    };
  },
  computed: {
    ...mapState("users", ["user", "role"]),
    ...mapGetters("users", ["isLoggedIn"]),
    ...mapGetters("settings", ["systemName"]),
  },

  mounted() {
    window.onpopstate = () => {
      if (router.currentRoute.path === '/') {
        this.enterDashboard();
      }
      if (window.$ && window.$('.modal').is(':visible')) {
        window.$(".modal").modal("hide");
      }
    };
  },

  created() {
    this.loadSavedLocale();
    this.fetchSettings();
    document.title = computeTitle(this.$route, this.isLoggedIn);

    if (this.isLoggedIn) {
      ApiService.setAuthHeader();

      const appElement = document.getElementById('app');
      if (appElement && appElement.dataset.initialRoute) {
        const initialRoute = appElement.dataset.initialRoute;
        if (initialRoute !== '/' && initialRoute !== this.$route.path) {
          this.$router.replace(initialRoute);
          return;
        }
      }

      this.enterDashboard();
    }

    eBus.$on('locale-changed', (locale) => {
      this.currentLocale = locale;
      document.title = computeTitle(this.$route, this.isLoggedIn);
    });
  },
  beforeDestroy() {
    eBus.$off('locale-changed');
  },

  methods: {
    ...mapActions("users", ["enterDashboard"]),
    ...mapActions("settings", ["fetchSettings"]),

    loadSavedLocale() {
      const savedLocale = localStorage.getItem('locale');
      if (savedLocale) {
        Lang.setLocale(savedLocale);
        setHtmlLangDir(savedLocale);
      } else {
        setHtmlLangDir(getCurrentLang());
      }
    },
  },
  watch: {
    systemName(newVal) {
      document.title = computeTitle(this.$route, this.isLoggedIn);
    },
    $route(to) {
      document.title = computeTitle(to, this.isLoggedIn);
    }
  }
};
</script>

<style scoped>
[v-cloak] { display: none; }
</style>
